import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Save, User, FileText, Building } from "lucide-react";

export default function ProfilePage() {
  const { user, checkAuth } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");

  // Billing
  const [street, setStreet] = useState("");
  const [streetNumber, setStreetNumber] = useState("");
  const [zip, setZip] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [fiscalCode, setFiscalCode] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [pec, setPec] = useState("");
  const [sdi, setSdi] = useState("");

  const fetchProfile = useCallback(async () => {
    try {
      const p = await api.get("/profile");
      setProfile(p);
      setFirstName(p.first_name || "");
      setLastName(p.last_name || "");
      setPhone(p.phone || "");
      const b = p.billing || {};
      setStreet(b.street || "");
      setStreetNumber(b.street_number || "");
      setZip(b.zip || "");
      setCity(b.city || "");
      setProvince(b.province || "");
      setFiscalCode(b.fiscal_code || "");
      setVatNumber(b.vat_number || "");
      setPec(b.pec || "");
      setSdi(b.sdi || "");
    } catch (e) {
      toast.error("Errore nel caricamento del profilo");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/profile", {
        first_name: firstName,
        last_name: lastName,
        phone,
        billing: {
          street, street_number: streetNumber, zip, city, province,
          fiscal_code: fiscalCode, vat_number: vatNumber, pec, sdi
        }
      });
      toast.success("Profilo aggiornato");
      await checkAuth();
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-green-700" /></div>;
  }

  const isSocial = profile?.auth_provider && profile.auth_provider !== "email";

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="profile-page">
      <div>
        <p className="text-green-700 text-xs font-bold uppercase tracking-widest mb-1">Account</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase tracking-tight text-slate-900">Il Mio Profilo</h1>
      </div>

      {/* Personal Info */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
            <User className="h-5 w-5 text-green-700" /> Informazioni Personali
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 pb-4 border-b border-slate-100">
            {profile?.picture ? (
              <img src={profile.picture} alt="" className="h-16 w-16 rounded-full object-cover border-2 border-slate-200" />
            ) : (
              <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
                <User className="h-7 w-7 text-green-700" />
              </div>
            )}
            <div>
              <p className="font-medium text-slate-900">{profile?.email}</p>
              {isSocial && (
                <Badge className="rounded-sm text-xs bg-slate-100 text-slate-600 hover:bg-slate-100 mt-1">
                  Accesso con {profile.auth_provider === "google" ? "Google" : profile.auth_provider === "facebook" ? "Facebook" : profile.auth_provider === "apple" ? "Apple" : profile.auth_provider === "amazon" ? "Amazon" : profile.auth_provider}
                </Badge>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide">Nome</Label>
              <Input data-testid="profile-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="mt-1 rounded-sm" placeholder="Mario" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide">Cognome</Label>
              <Input data-testid="profile-last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} className="mt-1 rounded-sm" placeholder="Rossi" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide">Email</Label>
              <Input value={profile?.email || ""} disabled className="mt-1 rounded-sm bg-slate-50 text-slate-500" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide">Telefono</Label>
              <Input data-testid="profile-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 rounded-sm" placeholder="+39 333 1234567" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Billing Info */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
            <Building className="h-5 w-5 text-green-700" /> Indirizzo di Fatturazione
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wide">Via</Label>
              <Input data-testid="profile-street" value={street} onChange={(e) => setStreet(e.target.value)} className="mt-1 rounded-sm" placeholder="Via Roma" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide">Numero</Label>
              <Input data-testid="profile-street-number" value={streetNumber} onChange={(e) => setStreetNumber(e.target.value)} className="mt-1 rounded-sm" placeholder="10" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide">CAP</Label>
              <Input data-testid="profile-zip" value={zip} onChange={(e) => setZip(e.target.value)} className="mt-1 rounded-sm" placeholder="00100" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide">Citta</Label>
              <Input data-testid="profile-city" value={city} onChange={(e) => setCity(e.target.value)} className="mt-1 rounded-sm" placeholder="Roma" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide">Provincia</Label>
              <Input data-testid="profile-province" value={province} onChange={(e) => setProvince(e.target.value)} className="mt-1 rounded-sm" placeholder="RM" maxLength={2} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fiscal Info */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
            <FileText className="h-5 w-5 text-green-700" /> Dati Fiscali
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide">Codice Fiscale</Label>
              <Input data-testid="profile-fiscal-code" value={fiscalCode} onChange={(e) => setFiscalCode(e.target.value.toUpperCase())} className="mt-1 rounded-sm" placeholder="RSSMRA80A01H501Z" maxLength={16} />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide">Partita IVA</Label>
              <Input data-testid="profile-vat" value={vatNumber} onChange={(e) => setVatNumber(e.target.value)} className="mt-1 rounded-sm" placeholder="01234567890" maxLength={11} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide">PEC</Label>
              <Input data-testid="profile-pec" value={pec} onChange={(e) => setPec(e.target.value)} className="mt-1 rounded-sm" placeholder="azienda@pec.it" type="email" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide">Codice SDI</Label>
              <Input data-testid="profile-sdi" value={sdi} onChange={(e) => setSdi(e.target.value.toUpperCase())} className="mt-1 rounded-sm" placeholder="0000000" maxLength={7} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex justify-end">
        <Button
          data-testid="profile-save-btn"
          onClick={saveProfile}
          disabled={saving}
          className="bg-green-700 hover:bg-green-800 text-white font-bold uppercase tracking-wide rounded-sm gap-2 px-8"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Salva Profilo
        </Button>
      </div>
    </div>
  );
}
